import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { getTomorrowDate, getMinCheckoutDate, isDateInPast, validateDateRange, validateDateRangeWithTimes, formatDateInput } from "@/lib/date-utils";
import { useQuery } from "@tanstack/react-query";
import { format, addDays } from "date-fns";

// Types for booking state
export interface BookingState {
  checkIn: string;
  checkOut: string;
  guests: string;
  propertyId?: string;
  propertySlug?: string;
}

// Property details for time-aware validation
interface PropertyDetails {
  check_in_time?: string;
  check_out_time?: string;
}

export interface BookingContextType {
  // Current booking state
  checkIn: string;
  checkOut: string;
  guests: string;
  propertyId: string;
  propertySlug: string;
  
  // Property details for validation
  propertyCheckoutTime?: string;
  
  // Setters for updating state
  setCheckIn: (date: string) => void;
  setCheckOut: (date: string) => void;
  setGuests: (guests: string) => void;
  setProperty: (propertyId: string, propertySlug: string) => void;
  
  // Helper functions
  updateBookingDates: (checkIn: string, checkOut?: string, considerCheckoutTime?: boolean) => void;
  resetBooking: () => void;
  
  // Validation helpers
  isValidBooking: () => boolean;
  hasValidDates: () => boolean;
  getDateValidationErrors: () => string[];
}

// Create the context
const BookingContext = createContext<BookingContextType | undefined>(undefined);

// Storage key for persisting booking data
const BOOKING_STORAGE_KEY = 'vacation-rental-booking-state';

// Normalize any persisted booking dates to plain date-only "YYYY-MM-DD" strings.
// Older sessions may have stored full ISO timestamps (from a previous timestamp-based
// picker); formatDateInput converts those to the local calendar day so the entire
// pipeline stays timezone-neutral.
const normalizeStoredDates = (parsed: any) => {
  if (parsed && typeof parsed === 'object') {
    if (parsed.checkIn) parsed.checkIn = formatDateInput(parsed.checkIn) || "";
    if (parsed.checkOut) parsed.checkOut = formatDateInput(parsed.checkOut) || "";
  }
  return parsed;
};

function withStoredProperty(
  dates: { checkIn: string; checkOut: string; guests: string },
  parsed?: any,
): BookingState {
  return {
    ...dates,
    propertyId: typeof parsed?.propertyId === "string" ? parsed.propertyId : "",
    propertySlug: typeof parsed?.propertySlug === "string" ? parsed.propertySlug : "",
  };
}

// Empty booking state - form starts completely empty for user selection
const getEmptyBookingState = (): BookingState => {
  console.log('🗓️ Starting with empty booking form - visitors will select their own dates');
  
  return {
    checkIn: "",
    checkOut: "",
    guests: "2",
    propertyId: "",
    propertySlug: "",
  };
};

// Default booking values - only used when we need valid dates for validation
const getDefaultBookingState = (): BookingState => {
  const tomorrow = getTomorrowDate();
  const dayAfterTomorrow = getMinCheckoutDate(tomorrow);
  
  console.log('🗓️ Generating fallback booking state for validation:', { 
    today: new Date().toISOString().split('T')[0], 
    tomorrow, 
    dayAfterTomorrow 
  });
  
  // Ensure we never return past dates
  if (isDateInPast(tomorrow)) {
    console.warn('⚠️ Tomorrow date is in past, using fallback');
    const now = new Date();
    const safeTomorrow = format(addDays(now, 1), 'yyyy-MM-dd');
    const safeCheckout = format(addDays(now, 2), 'yyyy-MM-dd');
    return {
      checkIn: safeTomorrow,
      checkOut: safeCheckout,
      guests: "2",
      propertyId: "",
      propertySlug: "",
    };
  }
  
  return {
    checkIn: tomorrow,
    checkOut: dayAfterTomorrow,
    guests: "2",
    propertyId: "",
    propertySlug: "",
  };
};

// Load booking state from localStorage/sessionStorage with improved validation
const loadBookingState = (): BookingState => {
  // Return empty state by default - users should select their own dates
  const emptyState = getEmptyBookingState();
  
  if (typeof window === 'undefined') return emptyState;
  
  try {
    // Try sessionStorage first (for current session)
    const sessionData = sessionStorage.getItem(BOOKING_STORAGE_KEY);
    if (sessionData) {
      const parsed = normalizeStoredDates(JSON.parse(sessionData));
      
      const savedGuests = parsed.guests || "2";
      
      // Check if check-in date is in the past - if so, clear dates but preserve guests
      if (parsed.checkIn && isDateInPast(parsed.checkIn)) {
        console.log('Clearing past booking dates from session storage, preserving guests:', savedGuests);
        sessionStorage.removeItem(BOOKING_STORAGE_KEY);
        return withStoredProperty({ checkIn: "", checkOut: "", guests: savedGuests }, parsed);
      }
      
      // If both dates are present, validate the range
      if (parsed.checkIn && parsed.checkOut) {
        const validation = validateDateRange(parsed.checkIn, parsed.checkOut);
        
        if (validation.isValid) {
          return withStoredProperty({
            checkIn: parsed.checkIn,
            checkOut: parsed.checkOut,
            guests: savedGuests
          }, parsed);
        } else {
          console.log('Session dates invalid, clearing but preserving guests:', savedGuests);
          sessionStorage.removeItem(BOOKING_STORAGE_KEY);
          return withStoredProperty({ checkIn: "", checkOut: "", guests: savedGuests }, parsed);
        }
      }
      
      // Return partial state as-is (checkin only, or checkout only)
      return withStoredProperty({
        checkIn: parsed.checkIn || "",
        checkOut: parsed.checkOut || "",
        guests: savedGuests
      }, parsed);
    }
    
    // Fall back to localStorage (persistent across sessions)
    const localData = localStorage.getItem(BOOKING_STORAGE_KEY);
    if (localData) {
      const parsed = normalizeStoredDates(JSON.parse(localData));
      const savedGuests = parsed.guests || "2";
      
      // Check if check-in date is in the past - if so, clear dates but preserve guests
      if (parsed.checkIn && isDateInPast(parsed.checkIn)) {
        console.log('Clearing past booking dates from local storage, preserving guests:', savedGuests);
        localStorage.removeItem(BOOKING_STORAGE_KEY);
        return withStoredProperty({ checkIn: "", checkOut: "", guests: savedGuests }, parsed);
      }
      
      // If both dates are present, validate the range
      if (parsed.checkIn && parsed.checkOut) {
        const validation = validateDateRange(parsed.checkIn, parsed.checkOut);
        
        if (validation.isValid) {
          return withStoredProperty({
            checkIn: parsed.checkIn,
            checkOut: parsed.checkOut,
            guests: savedGuests
          }, parsed);
        } else {
          console.log('Local dates invalid, clearing but preserving guests:', savedGuests);
          localStorage.removeItem(BOOKING_STORAGE_KEY);
          return withStoredProperty({ checkIn: "", checkOut: "", guests: savedGuests }, parsed);
        }
      }
      
      // Return partial state as-is (checkin only, or checkout only)
      return withStoredProperty({
        checkIn: parsed.checkIn || "",
        checkOut: parsed.checkOut || "",
        guests: savedGuests
      }, parsed);
    }
  } catch (error) {
    console.warn('Error loading booking state from storage:', error);
    // Clear potentially corrupted storage
    try {
      sessionStorage.removeItem(BOOKING_STORAGE_KEY);
      localStorage.removeItem(BOOKING_STORAGE_KEY);
    } catch (e) {
      console.warn('Error clearing storage:', e);
    }
  }
  
  // Return empty state if no valid stored state - let users choose their own dates
  return emptyState;
};

// Save booking state to both localStorage and sessionStorage
const saveBookingState = (state: BookingState) => {
  if (typeof window === 'undefined') return;
  try {
    const stateData = JSON.stringify(state);
    sessionStorage.setItem(BOOKING_STORAGE_KEY, stateData);
    localStorage.setItem(BOOKING_STORAGE_KEY, stateData);
  } catch (error) {
    console.warn('Error saving booking state to storage:', error);
  }
};

interface BookingProviderProps {
  children: ReactNode;
}

export function BookingProvider({ children }: BookingProviderProps) {
  // Initialize booking state - try to load valid saved state, otherwise start fresh
  const initializeBookingState = (): BookingState => {
    if (typeof window === 'undefined') return getEmptyBookingState();
    try {
      // Try sessionStorage first, then localStorage
      const sessionData = sessionStorage.getItem(BOOKING_STORAGE_KEY);
      const localData = localStorage.getItem(BOOKING_STORAGE_KEY);
      const savedData = sessionData || localData;
      
      if (savedData) {
        try {
          const parsed = normalizeStoredDates(JSON.parse(savedData));
          
          // Validate the parsed data structure
          if (typeof parsed !== 'object' || parsed === null) {
            throw new Error('Invalid booking state structure');
          }
          
          // Check if we have actual user-selected dates (not empty strings)
          const hasUserDates = parsed.checkIn && typeof parsed.checkIn === 'string' && parsed.checkIn.trim() !== '';
          
          // Always preserve guests value from storage
          const savedGuests = parsed.guests || "2";
          
          if (hasUserDates) {
            // Validate dates are not in the past
            if (isDateInPast(parsed.checkIn)) {
              console.log('📅 Saved check-in date is in the past, preserving guests:', savedGuests);
              sessionStorage.removeItem(BOOKING_STORAGE_KEY);
              localStorage.removeItem(BOOKING_STORAGE_KEY);
              return withStoredProperty({ checkIn: "", checkOut: "", guests: savedGuests }, parsed);
            }
            
            // Check if we also have a checkout date
            const hasCheckout = parsed.checkOut && typeof parsed.checkOut === 'string' && parsed.checkOut.trim() !== '';
            
            if (hasCheckout) {
              // Validate the full date range only when both dates exist
              const validation = validateDateRange(parsed.checkIn, parsed.checkOut);
              if (validation.isValid) {
                console.log('📦 Loaded valid complete booking state from storage:', parsed);
                return withStoredProperty({
                  checkIn: parsed.checkIn,
                  checkOut: parsed.checkOut,
                  guests: savedGuests
                }, parsed);
              } else {
                console.log('📅 Saved dates invalid, preserving guests:', savedGuests);
                sessionStorage.removeItem(BOOKING_STORAGE_KEY);
                localStorage.removeItem(BOOKING_STORAGE_KEY);
                return withStoredProperty({ checkIn: "", checkOut: "", guests: savedGuests }, parsed);
              }
            } else {
              // Only check-in date is saved (no checkout) - preserve partial selection
              console.log('📦 Loaded partial booking state (check-in only) from storage:', parsed.checkIn);
              return withStoredProperty({
                checkIn: parsed.checkIn,
                checkOut: "",
                guests: savedGuests
              }, parsed);
            }
          } else {
            // Empty dates in storage - preserve guests selection if present
            console.log('🏠 Storage has empty dates, preserving guests:', savedGuests);
            return withStoredProperty({
              checkIn: "",
              checkOut: "",
              guests: savedGuests
            }, parsed);
          }
        } catch (parseError) {
          // Malformed JSON - clear corrupted storage
          console.warn('🧹 Clearing corrupted booking storage:', parseError);
          sessionStorage.removeItem(BOOKING_STORAGE_KEY);
          localStorage.removeItem(BOOKING_STORAGE_KEY);
          return getEmptyBookingState();
        }
      }
    } catch (storageError) {
      console.warn('Error accessing storage:', storageError);
    }
    
    // No saved data, start with empty form for fresh visitors
    console.log('🏠 Starting with empty booking form');
    return getEmptyBookingState();
  };
  
  // Hydrate with what the SERVER rendered, then restore from storage after mount.
  //
  // This used to initialise straight from storage. On the server that returns an
  // empty form; in the browser it returns the visitor's saved dates — so React's
  // first client render did not match the HTML it was hydrating, which is React
  // error #418. The difference is structural (a date range renders different
  // nodes to an empty one), so React gave up and re-mounted the whole root:
  // error #423, on every load of the booking page.
  //
  // A re-mounting root is not cosmetic on the page where people pay. It discards
  // and rebuilds the form mid-interaction, and it was firing on the site's
  // highest-value route.
  //
  // Starting empty and restoring in an effect costs one frame with a blank form.
  // That is the accepted trade: hydration has to be deterministic, and the only
  // value both sides can agree on before mount is the one the server produced.
  const [bookingState, setBookingState] = useState<BookingState>(() => getEmptyBookingState());
  const hasRestoredFromStorage = useRef(false);

  useEffect(() => {
    if (hasRestoredFromStorage.current) return;
    hasRestoredFromStorage.current = true;

    const restored = initializeBookingState();
    // Only disturb state if storage actually held something, so a fresh visitor
    // does not get a pointless second render.
    if (restored.checkIn || restored.checkOut || restored.guests !== getEmptyBookingState().guests) {
      setBookingState(restored);
    }
  }, []);
  
  // Flag to prevent auto-correction during manual date selection
  const [isManualSelection, setIsManualSelection] = useState(false);
  
  // Ref to prevent infinite loops in auto-correction
  const autoCorrectionInProgressRef = useRef(false);
  
  // Individual state values for easy access
  const { checkIn, checkOut, guests, propertyId, propertySlug } = bookingState;
  
  // Fetch property details for time-aware validation
  const { data: propertyDetails } = useQuery<PropertyDetails>({
    queryKey: propertySlug
      ? ["/api/properties", propertySlug]
      : ["/api/property/public"],
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Switching homes keeps the dates: a guest whose dates are taken at one home
  // goes straight to the other with the same dates, and the quote and
  // availability are re-checked for that home.
  const setProperty = useCallback((nextId: string, nextSlug: string) => {
    setBookingState((prev) => ({ ...prev, propertyId: nextId, propertySlug: nextSlug }));
  }, []);
  
  // Save to storage whenever state changes — but never before the restore has run.
  //
  // This effect fires on mount too, and mount state is deliberately empty so that
  // hydration matches the server. Writing that empty state straight back would
  // erase the visitor's saved dates before the restore below ever reads them:
  // the form would come back blank every single time, which is worse than the
  // hydration error this arrangement exists to fix.
  useEffect(() => {
    if (!hasRestoredFromStorage.current) return;
    saveBookingState(bookingState);
  }, [bookingState]);
  
  // Auto-correct dates only when needed, with loop prevention
  const autoCorrectDatesIfNeeded = useCallback(() => {
    // Prevent infinite loops or auto-correction on manual selection
    if (autoCorrectionInProgressRef.current || isManualSelection) {
      return;
    }
    
    // Don't auto-correct if form is empty - let users select their own dates
    if (!checkIn && !checkOut) {
      return;
    }
    
    // Only validate if both dates are present
    if (!checkIn || !checkOut) {
      return;
    }
    
    // Use time-aware validation if property checkout time is available
    const validation = propertyDetails?.check_out_time 
      ? validateDateRangeWithTimes(checkIn, checkOut, propertyDetails.check_out_time)
      : validateDateRange(checkIn, checkOut);
      
    if (!validation.isValid) {
      autoCorrectionInProgressRef.current = true;
      console.log('Auto-correcting invalid booking dates:', validation.errors);
      
      // Check if check-in date is in the past
      if (isDateInPast(checkIn)) {
        // Clear invalid past dates instead of auto-filling
        setBookingState(prev => ({ 
          ...prev, 
          checkIn: "",
          checkOut: ""
        }));
      } else if (checkIn && checkOut && checkOut <= checkIn) {
        // Only fix checkout if it exists but is before/same as checkin.
        // Date-only "YYYY-MM-DD" strings compare correctly lexicographically,
        // so no Date/UTC conversion is needed here.
        // Do NOT auto-fill checkout if it's intentionally blank
        const newCheckOut = getMinCheckoutDate(checkIn);
        if (newCheckOut && newCheckOut !== checkOut) {
          setBookingState(prev => ({ ...prev, checkOut: newCheckOut }));
        }
      }
      
      // Reset the flag after a short delay
      setTimeout(() => {
        autoCorrectionInProgressRef.current = false;
      }, 100);
    }
  }, [checkIn, checkOut, propertyDetails?.check_out_time, isManualSelection]);
  
  // Run validation only when property details change or dates are entered
  useEffect(() => {
    console.log('🔍 Checking booking state on mount:', { checkIn, checkOut, guests });
    
    // Allow empty dates - only validate if dates are actually entered
    if (!checkIn && !checkOut) {
      console.log('✅ Booking form is empty - ready for user input');
      return;
    }
    
    // Only verify dates are not in the past if they have been entered
    if (checkIn && isDateInPast(checkIn)) {
      console.log('❌ Detected past check-in date, clearing:', checkIn);
      setBookingState(prev => ({ ...prev, checkIn: "", checkOut: "" }));
    } else if (checkOut && isDateInPast(checkOut)) {
      console.log('❌ Detected past check-out date, clearing:', checkOut);
      setBookingState(prev => ({ ...prev, checkOut: "" }));
    } else if (checkIn || checkOut) {
      console.log('✅ Booking dates are valid:', { checkIn, checkOut });
    }
  }, [propertyDetails?.check_out_time]); // Only depend on property details
  
  // Setters that update the full state with auto-correction prevention
  const setCheckIn = useCallback((date: string) => {
    autoCorrectionInProgressRef.current = true;
    setBookingState(prev => ({ ...prev, checkIn: date }));
    setTimeout(() => {
      autoCorrectionInProgressRef.current = false;
    }, 100);
  }, []);
  
  const setCheckOut = useCallback((date: string) => {
    autoCorrectionInProgressRef.current = true;
    setBookingState(prev => ({ ...prev, checkOut: date }));
    setTimeout(() => {
      autoCorrectionInProgressRef.current = false;
    }, 100);
  }, []);
  
  const setGuests = useCallback((guests: string) => {
    setBookingState(prev => ({ ...prev, guests }));
  }, []);
  
  // Helper to update both dates at once with checkout time consideration
  const updateBookingDates = useCallback((newCheckIn: string, newCheckOut?: string, considerCheckoutTime?: boolean) => {
    // Set manual selection flag and auto-correction prevention
    setIsManualSelection(true);
    autoCorrectionInProgressRef.current = true;
    
    // Determine the checkout date/timestamp
    // IMPORTANT: If newCheckOut is undefined, keep it empty - user must explicitly select checkout
    let finalCheckOut: string = "";
    
    if (newCheckOut) {
      // Use the provided checkout date (plain "YYYY-MM-DD" string)
      finalCheckOut = newCheckOut;
      
      // If we should consider checkout time and have property details, guard against
      // a same-day (or earlier) checkout. Date-only strings compare directly, so no
      // Date/UTC conversion is needed.
      if (considerCheckoutTime && finalCheckOut <= newCheckIn) {
        // Bump checkout to the day after check-in to prevent same-day conflicts
        finalCheckOut = getMinCheckoutDate(newCheckIn);
      }
    }
    // No else case - if newCheckOut is undefined, finalCheckOut stays empty
    // User must explicitly select their checkout date
    
    setBookingState(prev => ({
      ...prev,
      checkIn: newCheckIn,
      checkOut: finalCheckOut
    }));
    
    // Clear flags after a short delay to allow normal validation to resume
    setTimeout(() => {
      setIsManualSelection(false);
      autoCorrectionInProgressRef.current = false;
    }, 500);
  }, [propertyDetails?.check_out_time]);
  
  // Reset to empty state (clears dates; guests back to default 2) and drop persisted storage.
  // The home stays: Clear all on a listing page must not send the next quote,
  // rate or reservation to the default home instead of the one on screen.
  const resetBooking = useCallback(() => {
    autoCorrectionInProgressRef.current = true;
    setIsManualSelection(true);
    const empty = getEmptyBookingState();
    setBookingState((prev) => ({ ...empty, propertyId: prev.propertyId, propertySlug: prev.propertySlug }));
    if (typeof window !== "undefined") {
      try {
        sessionStorage.removeItem(BOOKING_STORAGE_KEY);
        localStorage.removeItem(BOOKING_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
    setTimeout(() => {
      setIsManualSelection(false);
      autoCorrectionInProgressRef.current = false;
    }, 100);
  }, []);
  
  // Validation helpers with improved error handling
  const hasValidDates = useCallback((): boolean => {
    // Use time-aware validation if property checkout time is available
    const validation = propertyDetails?.check_out_time 
      ? validateDateRangeWithTimes(checkIn, checkOut, propertyDetails.check_out_time)
      : validateDateRange(checkIn, checkOut);
    return validation.isValid;
  }, [checkIn, checkOut, propertyDetails?.check_out_time]);
  
  const getDateValidationErrors = useCallback((): string[] => {
    // Use time-aware validation if property checkout time is available
    const validation = propertyDetails?.check_out_time 
      ? validateDateRangeWithTimes(checkIn, checkOut, propertyDetails.check_out_time)
      : validateDateRange(checkIn, checkOut);
    return validation.errors;
  }, [checkIn, checkOut, propertyDetails?.check_out_time]);
  
  const isValidBooking = useCallback((): boolean => {
    if (!hasValidDates()) return false;
    
    const guestCount = parseInt(guests);
    return Boolean(guests && !isNaN(guestCount) && guestCount > 0);
  }, [hasValidDates, guests]);
  
  const contextValue: BookingContextType = {
    checkIn,
    checkOut,
    guests,
    propertyId: propertyId || "",
    propertySlug: propertySlug || "",
    propertyCheckoutTime: propertyDetails?.check_out_time,
    setCheckIn,
    setCheckOut,
    setGuests,
    setProperty,
    updateBookingDates,
    resetBooking,
    isValidBooking,
    hasValidDates,
    getDateValidationErrors,
  };
  
  return (
    <BookingContext.Provider value={contextValue}>
      {children}
    </BookingContext.Provider>
  );
}

// Custom hook to use the booking context
export function useBooking(): BookingContextType {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
}

// Export context for testing purposes
export { BookingContext };